import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useBackofficePageTitle } from '@/lib/page-title';
import { isUuid } from '@/lib/uuid';
import { copyTextToClipboard, PageHeader, type TableActionItem } from '@/components/shared';
import { usePersistedSearchParams } from '@/preferences';
import { fetchBackofficeOrganizations, fetchBackofficeUserByEmail } from './api';
import {
  CreateOrganizationWizardModal,
  OrganizationsFilters,
  OrganizationsPageActions,
  OrganizationsTable,
} from './components';
import type {
  BackofficeOrganizationRow,
  OrganizationSortBy,
  PricingTier,
  SortDirection,
} from './types';

const DEFAULT_LIMIT = 20;
const SORTABLE_FIELDS = new Set<OrganizationSortBy>([
  'name',
  'pricingTier',
  'updatedAt',
  'createdAt',
]);
const PRICING_TIER_VALUES = new Set<PricingTier>(['free_1', 'payg_1', 'ent_1']);
function readSortBy(value: string | null): OrganizationSortBy {
  return value && SORTABLE_FIELDS.has(value as OrganizationSortBy)
    ? (value as OrganizationSortBy)
    : 'updatedAt';
}

function readSortDirection(value: string | null): SortDirection {
  return value === 'asc' || value === 'desc' ? value : 'desc';
}

function readPricingTier(value: string | null): PricingTier | '' {
  return value && PRICING_TIER_VALUES.has(value as PricingTier) ? (value as PricingTier) : '';
}

export function BackofficeOrganizationsPage() {
  useBackofficePageTitle('Organizations');

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [organizations, setOrganizations] = useState<BackofficeOrganizationRow[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [creationNotice, setCreationNotice] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const loadingMoreRef = useRef(false);
  const refreshAbortControllerRef = useRef<AbortController | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const pricingTier = readPricingTier(searchParams.get('pricingTier'));
  const organizationId = searchParams.get('organizationId') ?? '';
  const owner = searchParams.get('ownerUserId') ?? '';
  const requestOrganizationId = isUuid(organizationId) ? organizationId : '';
  const sortBy = readSortBy(searchParams.get('sortBy'));
  const sortDirection = readSortDirection(searchParams.get('sortDirection'));

  const requestParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set('sortBy', sortBy);
    params.set('sortDirection', sortDirection);
    if (pricingTier) params.set('pricingTier', pricingTier);
    if (requestOrganizationId) params.set('organizationId', requestOrganizationId);
    return params;
  }, [pricingTier, requestOrganizationId, sortBy, sortDirection]);

  const hasMore = organizations.length < total;

  const loadOrganizations = useCallback(
    async (offset: number, signal: AbortSignal) => {
      const params = new URLSearchParams(requestParams);
      const trimmedOwner = owner.trim();
      if (trimmedOwner) {
        if (isUuid(trimmedOwner)) {
          params.set('ownerUserId', trimmedOwner);
        } else {
          const lookup = await fetchBackofficeUserByEmail(trimmedOwner, signal);
          if (!lookup) {
            setTotal(0);
            setOrganizations((current) => (offset === 0 ? [] : current));
            return;
          }
          params.set('ownerUserId', lookup.user.id);
        }
      }
      params.set('limit', String(DEFAULT_LIMIT));
      params.set('offset', String(offset));

      const response = await fetchBackofficeOrganizations(params, signal);
      setTotal(response.page.total);
      setOrganizations((current) =>
        offset === 0 ? response.organizations : [...current, ...response.organizations]
      );
    },
    [owner, requestParams]
  );

  const handleRefresh = useCallback(async () => {
    refreshAbortControllerRef.current?.abort();
    const controller = new AbortController();
    refreshAbortControllerRef.current = controller;

    setRefreshing(true);
    setError(null);
    setLoadMoreError(null);

    try {
      await loadOrganizations(0, controller.signal);
    } catch (caught: unknown) {
      if (caught instanceof DOMException && caught.name === 'AbortError') {
        return;
      }
      setOrganizations([]);
      setTotal(0);
      setError(caught instanceof Error ? caught.message : 'Failed to refresh organizations');
    } finally {
      if (refreshAbortControllerRef.current === controller) {
        refreshAbortControllerRef.current = null;
      }
      if (!controller.signal.aborted) {
        setRefreshing(false);
      }
    }
  }, [loadOrganizations]);

  const tableActions = useMemo<TableActionItem[]>(
    () => [
      {
        id: 'refresh',
        label: 'Refresh',
        loadingLabel: 'Refreshing',
        icon: RefreshCw,
        onSelect: () => {
          void handleRefresh();
        },
        placement: 'fixed-inline',
        disabled: initialLoading,
        isLoading: refreshing,
      },
    ],
    [handleRefresh, initialLoading, refreshing]
  );

  const handleCreateOrganization = useCallback(() => {
    setCreationNotice(null);
    setCreateModalOpen(true);
  }, []);

  const handleCopyShareLink = useCallback(() => {
    const shareUrl = `${window.location.origin}${location.pathname}${location.search}${location.hash}`;
    void copyTextToClipboard(shareUrl);
  }, [location.hash, location.pathname, location.search]);

  useEffect(() => {
    const controller = new AbortController();
    setInitialLoading(true);
    setError(null);
    setLoadMoreError(null);

    loadOrganizations(0, controller.signal)
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') {
          return;
        }
        setOrganizations([]);
        setTotal(0);
        setError(caught instanceof Error ? caught.message : 'Failed to load organizations');
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setInitialLoading(false);
        }
      });

    return () => controller.abort();
  }, [loadOrganizations]);

  useEffect(() => {
    return () => refreshAbortControllerRef.current?.abort();
  }, []);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || initialLoading || !hasMore) {
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }

    const controller = new AbortController();
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) {
          return;
        }
        if (loadingMoreRef.current) {
          return;
        }

        observer.disconnect();
        loadingMoreRef.current = true;
        setLoadingMore(true);
        setLoadMoreError(null);
        loadOrganizations(organizations.length, controller.signal)
          .catch((caught: unknown) => {
            if (caught instanceof DOMException && caught.name === 'AbortError') {
              return;
            }
            setLoadMoreError(
              caught instanceof Error ? caught.message : 'Failed to load more organizations'
            );
          })
          .finally(() => {
            loadingMoreRef.current = false;
            if (!controller.signal.aborted) {
              setLoadingMore(false);
            }
          });
      },
      { rootMargin: '280px' }
    );

    observer.observe(target);

    return () => {
      controller.abort();
      observer.disconnect();
    };
  }, [hasMore, initialLoading, loadOrganizations, organizations.length]);

  usePersistedSearchParams({
    scope: 'list:organizations',
    keys: ['pricingTier', 'organizationId', 'ownerUserId', 'sortBy', 'sortDirection'],
    searchParams,
    setSearchParams,
  });

  function updateParams(updates: Record<string, string | null>) {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null || value === '') {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      }
      return next;
    });
  }

  function handleSortChange(nextSortBy: OrganizationSortBy) {
    const nextDirection = sortBy === nextSortBy && sortDirection === 'asc' ? 'desc' : 'asc';
    updateParams({ sortBy: nextSortBy, sortDirection: nextDirection });
  }

  return (
    <main className="min-h-[320px] bg-white p-6 md:p-8" aria-label="Backoffice main content">
      <div className="flex flex-col gap-5">
        <PageHeader
          actions={
            <OrganizationsPageActions
              onCreateOrganization={handleCreateOrganization}
              onCopyShareLink={handleCopyShareLink}
            />
          }
          showShareButton={false}
        >
          Organizations
        </PageHeader>

        {creationNotice ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-small text-emerald-800">
            {creationNotice}
          </div>
        ) : null}

        <section className="rounded-xl border border-neutral-200 bg-white">
          <OrganizationsFilters
            pricingTier={pricingTier}
            organizationId={organizationId}
            owner={owner}
            actions={tableActions}
            onPricingTierChange={(nextPricingTier) =>
              updateParams({ pricingTier: nextPricingTier })
            }
            onOrganizationIdChange={(value) => updateParams({ organizationId: value })}
            onOwnerChange={(value) => updateParams({ ownerUserId: value })}
          />

          {initialLoading ? (
            <div className="px-5 py-16 text-center text-body text-neutral-500">
              Loading organizations
            </div>
          ) : error ? (
            <div className="px-5 py-16 text-center text-body text-red-700">{error}</div>
          ) : organizations.length > 0 ? (
            <>
              <OrganizationsTable
                organizations={organizations}
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
              />
              <div ref={loadMoreRef} className="border-t border-neutral-200 px-5 py-4 text-center">
                {loadingMore ? (
                  <span className="text-small text-neutral-500">Loading more organizations</span>
                ) : loadMoreError ? (
                  <span className="text-small text-red-700">{loadMoreError}</span>
                ) : hasMore ? (
                  <span className="text-small text-neutral-500">Scroll to load more</span>
                ) : (
                  <span className="text-small text-neutral-500">
                    Showing {organizations.length} of {total}
                  </span>
                )}
              </div>
            </>
          ) : (
            <div className="px-5 py-16 text-center text-body text-neutral-500">
              No organizations found
            </div>
          )}
        </section>
        {createModalOpen ? (
          <CreateOrganizationWizardModal
            onClose={() => setCreateModalOpen(false)}
            onCreated={(response) => {
              setCreationNotice(`${response.organization.name} was created.`);
              void navigate(`/organizations/${response.organization.id}`);
            }}
          />
        ) : null}
      </div>
    </main>
  );
}
